import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import { rgbToHslShaderHelpers } from "./materials/shaders/rgbToHsl";
import { FullScreenQuad } from "./threejs/examples/jsm/postprocessing/FullScreenQuad";
import { UVVertexShader } from "./threejs/higharc/UVVertexShader";

export class MSAAResolver {
  readonly msaaBuffer: THREE.WebGLRenderTarget;
  private msaaOutputQuad: FullScreenQuad;
  private outputMaterial: THREE.ShaderMaterial;
  /**
   * @param useMSAA If set to false, this acts as simple backbuffer storage.
   */
  constructor(pixelWidth: number, pixelHeight: number, useMSAA: boolean) {
    this.msaaBuffer = new THREE.WebGLRenderTarget(pixelWidth, pixelHeight, {
      samples: useMSAA ? 4 : 0,
      stencilBuffer: true,
      colorSpace: THREE.SRGBColorSpace,
      resolveStencilBuffer: false,
    } as any); // typecast to any to support resolveStencilBuffer. Temporary until we upgrade three.js to a version where it's included in the types.
    const outputMaterial = new THREE.ShaderMaterial({
      name: "MSAAResolver outputMaterial",
      vertexShader: UVVertexShader,
      fragmentShader: `
      uniform sampler2D tDiffuse;
      uniform bool darkModeInvert;
      varying vec2 vUv;

      ${rgbToHslShaderHelpers}

      void main() {
        gl_FragColor = texture2D(tDiffuse, vUv);

        if (darkModeInvert) {
          vec3 hsl = rgb2hsl(gl_FragColor.rgb);
          hsl.z = 1.0 - hsl.z;
          gl_FragColor.rgb = hsl2rgb(hsl);
        }

        #include <colorspace_fragment>
      }
      `,
      uniforms: {
        tDiffuse: { value: this.msaaBuffer.texture },
        darkModeInvert: { value: false },
      },
    });
    this.outputMaterial = outputMaterial;
    this.msaaOutputQuad = new FullScreenQuad(outputMaterial);
  }

  setDarkMode(darkMode: boolean) {
    this.outputMaterial.uniforms.darkModeInvert.value = darkMode;
  }

  setSize(pixelWidth: number, pixelHeight: number): boolean {
    if (this.msaaBuffer.width !== pixelWidth || this.msaaBuffer.height !== pixelHeight) {
      this.msaaBuffer.setSize(pixelWidth, pixelHeight);
      return true;
    }
    return false;
  }

  resolve(renderer: THREE.WebGLRenderer, aabb?: AABB2) {
    if (aabb === undefined) {
      this.msaaOutputQuad.render(renderer);
      return;
    }
    this.msaaOutputQuad.renderPixelRectangle(renderer, aabb);
  }

  dispose() {
    this.msaaBuffer.dispose();
  }
}
